// Pure helpers for the dashboards UI (no React, no network): widget spec types,
// validation hints, pie roll-up, titles, field filtering, labels, spans.
import type { Condition } from '@/lib/expr'
import { categoryLabel } from '../home/model'
import type { FieldOption, FieldType, FormDefinition } from '../types'

export type WidgetType = 'kpi' | 'bar' | 'line' | 'pie' | 'table'
export type Bucket = 'day' | 'week' | 'month' | 'quarter' | 'year'
export type GroupKind = 'field' | 'status' | 'severity' | 'date' | 'form' | 'category' | 'department'
export type MetricOp = 'count' | 'sum' | 'avg' | 'min' | 'max' | 'percent'
export type Period = 'this_month' | 'this_quarter' | 'last_quarter' | 'ytd' | 'last_12_months' | 'all'
export interface DateRangeSpec { field?: string; preset?: Period; from?: string; to?: string }
export type SortMode = 'value_desc' | 'value_asc' | 'label'

export interface Widget {
    id: string
    title: string
    type: WidgetType
    /** Form id or slug; omitted/'' means all forms. */
    form?: string
    filters?: Condition
    dateRange?: DateRangeSpec
    groupBy?: { kind: GroupKind; key?: string; bucket?: Bucket }
    metric?: { op?: MetricOp; field?: string; of?: { statuses: string[] } }
    statuses?: string[]
    limit?: number
    sort?: SortMode
    columns?: string[]
}

/** Layout JSON. Unknown top-level keys must round-trip untouched. */
export interface Layout {
    widgets: Widget[]
    period?: Period
    filters?: string[]
    [k: string]: unknown
}

export interface Dashboard {
    id: string
    name: string
    description: string
    owner_id: string
    layout: Layout
    shared: 'private' | 'org'
    created_at: string
    updated_at: string
}

export interface ResultPoint { label: string; value: number; key?: string }
export interface ResultColumn { key: string; label: string; kind: 'core' | 'answer'; type?: string }
export interface ResultRow {
    id: string
    number: string
    title: string
    submitter_name: string
    status: string
    submitted_at: string | null
    answers: Record<string, unknown>
    [k: string]: unknown
}
export interface WidgetResult {
    widgetId: string
    type: WidgetType
    unit?: string
    total: number
    series: ResultPoint[]
    columns?: ResultColumn[]
    rows?: ResultRow[]
    truncated: boolean
    error?: string
}

export const WIDGET_TYPES: { value: WidgetType; label: string }[] = [
    { value: 'kpi', label: 'Number' },
    { value: 'bar', label: 'Bar' },
    { value: 'pie', label: 'Pie' },
    { value: 'line', label: 'Line' },
    { value: 'table', label: 'Table' },
]

export const STATUS_OPTIONS: FieldOption[] = [
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'in_review', label: 'In review' },
    { value: 'returned', label: 'Sent back' },
    { value: 'approved', label: 'Approved' },
    { value: 'refused', label: 'Refused' },
    { value: 'withdrawn', label: 'Withdrawn' },
]

export const MAX_COLUMNS = 6
export const MAX_LIMIT = 500
export const BUCKETS: Bucket[] = ['day', 'week', 'month', 'quarter', 'year']

/** The form fields a widget can reference (flattened from a definition). */
export interface FieldMeta { key: string; label: string; type: FieldType; options?: FieldOption[] }

export function fieldsOf(def: FormDefinition | null | undefined): FieldMeta[] {
    const out: FieldMeta[] = []
    for (const p of def?.pages ?? []) {
        for (const f of p.fields ?? []) {
            if (f.type === 'html') continue
            out.push({ key: f.key, label: f.label || f.key, type: f.type, options: f.options })
        }
    }
    return out
}

const GROUPABLE: FieldType[] = [
    'single_select', 'multi_select', 'yes_no', 'text', 'country', 'user_reference', 'date', 'datetime',
]
const isDateType = (t: FieldType) => t === 'date' || t === 'datetime'

/** Fields a widget may group by; line charts need date fields only. */
export function groupableFields(fields: FieldMeta[], widgetType?: WidgetType): FieldMeta[] {
    return fields.filter((f) => GROUPABLE.includes(f.type) && (widgetType !== 'line' || isDateType(f.type)))
}
export const dateFields = (fields: FieldMeta[]) => fields.filter((f) => isDateType(f.type))
export const numberFields = (fields: FieldMeta[]) => fields.filter((f) => f.type === 'number')

/** Default tile width in grid columns, derived from the widget type. */
export function defaultSpan(type: WidgetType): 1 | 2 | 3 {
    switch (type) {
        case 'kpi': case 'pie': return 1
        case 'bar': case 'line': return 2
        default: return 3
    }
}

const METRIC_WORD: Record<Exclude<MetricOp, 'count' | 'percent'>, string> = { sum: 'Total', avg: 'Average', min: 'Minimum', max: 'Maximum' }

export const PERIODS: { value: Period; label: string }[] = [
    { value: 'this_month', label: 'This month' },
    { value: 'this_quarter', label: 'This quarter' },
    { value: 'last_quarter', label: 'Last quarter' },
    { value: 'ytd', label: 'Year to date' },
    { value: 'last_12_months', label: 'Last 12 months' },
    { value: 'all', label: 'All time' },
]
export const CUSTOM_PERIOD = 'custom'
export type PeriodChoice = Period | typeof CUSTOM_PERIOD

export function periodLabel(p: string | undefined): string {
    if (p === CUSTOM_PERIOD) return 'Custom range'
    return PERIODS.find((x) => x.value === p)?.label ?? 'All time'
}
export const isPeriod = (p: unknown): p is Period => PERIODS.some((x) => x.value === p)

/**
 * The dateRange override a dashboard period selection sends. "All time" sends
 * no override, so widgets keep their own range; a custom range needs a bound.
 */
export function periodOverride(choice: PeriodChoice, from: string, to: string): DateRangeSpec | undefined {
    if (choice === CUSTOM_PERIOD) return from || to ? { ...(from ? { from } : {}), ...(to ? { to } : {}) } : undefined
    return choice === 'all' ? undefined : { preset: choice }
}

/** Hint for an unusable custom range (empty if fine). */
export function periodHint(choice: PeriodChoice, from: string, to: string): string {
    if (choice !== CUSTOM_PERIOD) return ''
    if (!from && !to) return 'Pick a start or end date.'
    if (from && to && from > to) return 'The start date is after the end date.'
    return ''
}

/** Percent values show one decimal at most ("12.5%", "40%"); others use formatNumber. */
export function formatValue(n: number, unit?: string): string {
    if (unit === '%') return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n)}%`
    return formatNumber(n)
}

/** Display label of a series point: humanised category, formatted date bucket, else the server label. */
export function pointLabel(w: Widget, p: ResultPoint): string {
    const g = w.groupBy
    if (g?.kind === 'category') return categoryLabel(p.key ?? p.label)
    if (g?.kind === 'date' || (g?.kind === 'field' && g.bucket)) return formatBucketLabel(p.key, bucketOf(w)) || p.label
    return p.label
}

const GROUP_WORD: Partial<Record<GroupKind, string>> = {
    status: 'status', severity: 'severity', form: 'form', category: 'category', department: 'business unit',
}

/** A readable title built from the spec; used when the author leaves it blank. */
export function defaultTitle(w: Widget, fields: FieldMeta[], formName = 'requests'): string {
    const label = (k?: string) => fields.find((f) => f.key === k)?.label ?? k ?? ''
    const op = w.metric?.op ?? 'count'
    const measure = op === 'count' ? '' : op === 'percent' ? 'Share of requests' : `${METRIC_WORD[op]} ${label(w.metric?.field)}`
    const g = w.groupBy
    const by = g?.kind === 'date' ? (g.bucket ?? 'month') : g?.kind === 'field' ? label(g.key) : g ? GROUP_WORD[g.kind] ?? '' : ''
    switch (w.type) {
        case 'kpi': return op === 'count' ? `Total ${formName}` : measure
        case 'table': return `Recent ${formName}`
        case 'line': return `${measure || 'Requests'} per ${by || 'month'}`
        default: return `${measure || 'Requests'} by ${by || 'category'}`.trim()
    }
}

/** Synthetic keys a filter may use (valid with or without a form). */
export const SYNTHETIC_KEYS = ['$status', '$form', '$department', '$finding_severity', '$submitted_at', '$form_version']

/** Keys referenced by a condition tree. */
export function conditionKeys(c: Condition | undefined): string[] {
    if (!c) return []
    if ('field' in c) return [c.field]
    if ('not' in c) return conditionKeys(c.not)
    return ('all' in c ? c.all : c.any).flatMap(conditionKeys)
}

/**
 * Cheap client-side checks mirroring backend dashboard.Validate so the builder
 * can explain what is missing before it calls the server.
 */
export function specHints(w: Widget, fields: FieldMeta[]): string[] {
    const out: string[] = []
    const noForm = !w.form
    const g = w.groupBy
    const needsGroup = w.type === 'bar' || w.type === 'pie' || w.type === 'line'
    if (needsGroup && !g) out.push('Choose what to group by.')
    if (g?.kind === 'field') {
        const f = fields.find((x) => x.key === g.key)
        if (noForm) out.push('Grouping by a question needs a single form.')
        else if (!g.key) out.push('Choose a question to group by.')
        else if (!f) out.push('That question is not on this form.')
        else if (!GROUPABLE.includes(f.type)) out.push('That question type cannot be grouped.')
        else if (w.type === 'line' && !isDateType(f.type)) out.push('Line charts need a date.')
    }
    if (w.type === 'line' && g && g.kind !== 'date' && g.kind !== 'field') out.push('Line charts need a date.')
    const op = w.metric?.op ?? 'count'
    if (op === 'percent') {
        if (!w.metric?.of?.statuses?.length) out.push('Choose at least one status for the share.')
    } else if (op !== 'count') {
        const f = fields.find((x) => x.key === w.metric?.field)
        if (noForm) out.push('Sum, average, minimum and maximum need a single form.')
        else if (!w.metric?.field) out.push(`Choose a number question to ${op}.`)
        else if (!f || f.type !== 'number') out.push('The metric needs a number question.')
    }
    if (noForm && (w.columns?.length ?? 0) > 0) out.push('Question columns need a single form.')
    if ((w.columns?.length ?? 0) > MAX_COLUMNS) out.push(`At most ${MAX_COLUMNS} columns.`)
    if (noForm && conditionKeys(w.filters).some((k) => !SYNTHETIC_KEYS.includes(k))) out.push('Filters on questions need a single form.')
    if (w.limit !== undefined && (w.limit < 1 || w.limit > MAX_LIMIT)) out.push(`Limit must be 1 to ${MAX_LIMIT}.`)
    return [...new Set(out)]
}

const emptyGroup = (c: Condition) => ('all' in c && c.all.length === 0) || ('any' in c && c.any.length === 0)

/** Normalise a spec for the wire: drop fields that do not apply to its type. */
export function cleanSpec(w: Widget, fields: FieldMeta[], formName?: string): Widget {
    const s: Widget = { id: w.id, title: w.title.trim() || defaultTitle(w, fields, formName), type: w.type }
    if (w.form) s.form = w.form
    if (w.filters && !emptyGroup(w.filters)) s.filters = w.filters
    if (w.dateRange && (w.dateRange.preset || w.dateRange.from || w.dateRange.to)) {
        s.dateRange = w.dateRange.preset ? { preset: w.dateRange.preset } : w.dateRange
    }
    if (w.type === 'bar' || w.type === 'pie' || w.type === 'line') {
        if (w.groupBy) {
            const g = w.groupBy
            const isDate = g.kind === 'date' || (g.kind === 'field' && isDateType(fields.find((f) => f.key === g.key)?.type ?? 'text'))
            s.groupBy = { kind: g.kind, ...(g.kind === 'field' ? { key: g.key } : {}), ...(isDate && g.bucket ? { bucket: g.bucket } : {}) }
        }
    }
    if (w.type !== 'table') {
        if (w.metric?.op === 'percent') s.metric = { op: 'percent', of: { statuses: w.metric.of?.statuses ?? [] } }
        else if (w.metric?.op && w.metric.op !== 'count') s.metric = { op: w.metric.op, field: w.metric.field }
    }
    if (w.statuses?.length) s.statuses = w.statuses
    if (w.type !== 'kpi' && w.limit) s.limit = w.limit
    if ((w.type === 'bar' || w.type === 'pie') && w.sort) s.sort = w.sort
    if (w.type === 'table' && w.form && w.columns?.length) s.columns = w.columns
    return s
}

/** Save payload for a layout: edits widgets, keeps every other key (period, filters, ...) as loaded. */
export function withWidgets(layout: Layout | undefined, widgets: Widget[]): Layout {
    return { ...(layout ?? {}), widgets }
}

/** Whether the dashboard shows a dashboard-level control (layout.filters). */
export const hasFilter = (layout: Layout | undefined, name: string) => Array.isArray(layout?.filters) && layout!.filters!.includes(name)

export interface PieSlice { label: string; value: number; key: string; percent: number; isOther: boolean }

/** Keep the top `max` slices and roll the rest into "Other". Zero/negative values are dropped. */
export function pieRollup(series: ResultPoint[], max = 6): PieSlice[] {
    const pts = series.filter((p) => p.value > 0).sort((a, b) => b.value - a.value)
    const total = pts.reduce((s, p) => s + p.value, 0)
    if (total === 0) return []
    const top = pts.slice(0, max)
    const rest = pts.slice(max)
    const slices: PieSlice[] = top.map((p) => ({ label: p.label, value: p.value, key: p.key ?? p.label, percent: p.value / total, isOther: false }))
    if (rest.length) {
        const v = rest.reduce((s, p) => s + p.value, 0)
        slices.push({ label: 'Other', value: v, key: '__other', percent: v / total, isOther: true })
    }
    return slices
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Format an ISO bucket start (UTC) for an axis/tooltip according to its bucket size. */
export function formatBucketLabel(key: string | undefined, bucket: Bucket | undefined): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(key ?? '')
    if (!m) return key ?? ''
    const y = Number(m[1]), mo = Number(m[2]) - 1, d = Number(m[3])
    switch (bucket) {
        case 'day': return `${d} ${MONTHS[mo]} ${y}`
        case 'week': return `Wk of ${d} ${MONTHS[mo]}`
        case 'quarter': return `Q${Math.floor(mo / 3) + 1} ${y}`
        case 'year': return String(y)
        default: return `${MONTHS[mo]} ${y}`
    }
}

/** The bucket a widget's date axis uses (backend default is month). */
export function bucketOf(w: Widget): Bucket {
    return w.groupBy?.bucket ?? 'month'
}

export function formatNumber(n: number): string {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n)
}

/** One-line text alternative for a result, used as the chart's aria-label. */
export function summarize(w: Widget, r: WidgetResult): string {
    const t = w.title
    if (r.error) return `${t}: could not load`
    if (w.type === 'kpi') return `${t}: ${formatValue(r.total, r.unit)}${r.unit && r.unit !== '%' ? ` ${r.unit}` : ''}`
    if (w.type === 'table') return `${t}: table of ${r.rows?.length ?? 0} rows`
    if (r.series.length === 0) return `${t}: no data`
    const top = [...r.series].sort((a, b) => b.value - a.value)[0]
    return `${t}: ${w.type} chart with ${r.series.length} values, total ${formatNumber(r.total)}; highest is ${pointLabel(w, top)} at ${formatValue(top.value, r.unit)}`
}

let seq = 0
export function newWidgetId(): string {
    seq += 1
    return `w${Date.now().toString(36)}${seq}${Math.random().toString(36).slice(2, 5)}`
}

/** Move an item by `delta`, clamped; returns the same array if nothing moves. */
export function moveItem<T>(list: T[], index: number, delta: number): T[] {
    const to = index + delta
    if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list
    const copy = list.slice()
    const [it] = copy.splice(index, 1)
    copy.splice(to, 0, it)
    return copy
}

/** sessionStorage key prefix: set before navigating to a dashboard to open it in edit mode. */
export const EDIT_FLAG = 'dashboard-edit:'
